import { useCallback, useEffect, useMemo, useState } from 'react'

import { clsx } from 'clsx'
import { Helmet } from 'react-helmet-async'
import { useNavigate } from 'react-router'
import { useSearchParams } from 'react-router-dom'

import { generateChatPath } from '@bettermode/common/utils'
import {
  GroupChatCreationPermission,
  PrivateMessagingOutgoingContact,
  RoleType,
} from '@tribeplatform/gql-client/types'
import {
  useAuthMember,
  useCreateGroupChat,
  useFindChatWithMember,
  useMember,
  useNetwork,
} from '@tribeplatform/react-sdk/hooks'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { MemberGroups } from '../../../CMS/types.js'
import { Form } from '../../../Form/index.js'
import { useI18n } from '../../../i18n/providers/I18nProvider.js'
import { defaultGroupChatAvatar, networkFields } from '../../constants.js'
import { NewChatFormFields } from '../../types.js'

import { NewChatForm } from './NewChatForm.js'

interface Props {
  className?: string
}

export const NewChat = ({ className }: Props) => {
  const { $t } = useI18n()
  const { data: authMember, isModerator } = useAuthMember()
  const { data: network } = useNetwork({ fields: networkFields })
  const navigate = useNavigate()
  const { mutateAsync: createGroupChat, isLoading: isCreatingGroup } =
    useCreateGroupChat({
      onSuccess(data) {
        navigate(generateChatPath(data._id), { replace: true })
      },
    })
  const { whoCanCreateGroups, whoCanMembersContact } =
    network?.settings?.privateMessaging || {}
  const isStaff =
    authMember?.role?.type === RoleType.moderator ||
    authMember?.role?.type === RoleType.admin
  const anyoneCanCreateGroups =
    whoCanCreateGroups === GroupChatCreationPermission.Anyone
  const membersCanContactAnyone =
    whoCanMembersContact === PrivateMessagingOutgoingContact.Anyone
  const canCreateGroup = isStaff || anyoneCanCreateGroups
  const reachableMemberGroup =
    membersCanContactAnyone || isModerator
      ? MemberGroups.all
      : MemberGroups.staff
  const [searchParams] = useSearchParams()
  const memberId = searchParams.get('member')
  const [isLoadingExistingChat, setIsLoadingExistingChat] = useState(!!memberId)
  const { data: chat } = useFindChatWithMember({
    variables: {
      recipientId: memberId,
    },
    useQueryOptions: {
      enabled: !!memberId,
      refetchOnWindowFocus: false,
      retry: false,
      onSettled: () => setIsLoadingExistingChat(false),
    },
  })
  const { data: member, isFetching: isLoadingMember } = useMember({
    variables: {
      id: memberId,
    },
    useQueryOptions: {
      enabled: !!memberId && !chat,
      refetchOnWindowFocus: false,
      retry: false,
    },
  })

  const defaultValues = useMemo(() => {
    if (member && !canCreateGroup) {
      return {
        name: '',
        members: member,
        avatar: defaultGroupChatAvatar,
      } as NewChatFormFields
    }

    return {
      name: '',
      members: member ? [member] : undefined,
      avatar: defaultGroupChatAvatar,
    } as NewChatFormFields
  }, [member, canCreateGroup])

  useEffect(() => {
    if (chat) {
      navigate(generateChatPath(chat._id), { replace: true })
    }
  }, [chat, navigate])

  const onSubmit = useCallback(
    async ({ name, members, avatar }: NewChatFormFields) => {
      const memberIds = Array.isArray(members)
        ? members.map(({ id }) => id)
        : [members.id]
      try {
        const { _id: newGroupId } = await createGroupChat({
          input: {
            name,
            memberIds,
            avatarId: avatar?.id ?? null,
          },
        })
        navigate(generateChatPath(newGroupId), { replace: true })
      } catch (error) {
        toast({
          title: $t({
            defaultMessage: 'Failed to create group chat',
            id: 'Messaging.NewGroupChatModal.Error',
          }),
          status: 'error',
        })
      }
    },
    [$t, createGroupChat, navigate],
  )

  if (isLoadingMember || isLoadingExistingChat) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="flex-1 flex flex-col items-center justify-center p-4">
          <div className="h-5 bg-skeleton rounded-base w-32 mb-2 animate-pulse"></div>
          <div className="h-5 bg-skeleton rounded-base w-32 mb-2 animate-pulse"></div>
          <div className="h-4 bg-skeleton rounded-base w-64 animate-pulse"></div>
        </div>
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>
          {$t({
            defaultMessage: 'New message',
            id: 'Generics.NewMessage',
          })}
        </title>
      </Helmet>
      <div
        className={clsx(
          'w-full h-full flex flex-col bg-surface sm:rounded-base',
          className,
        )}
      >
        <Form<NewChatFormFields>
          className="space-y-10 h-full"
          onSubmit={onSubmit}
          defaultValues={defaultValues}
        >
          <NewChatForm
            canCreateGroup={canCreateGroup}
            reachableMemberGroup={reachableMemberGroup}
            isCreatingGroup={isCreatingGroup}
          />
        </Form>
      </div>
    </>
  )
}
